import { Observable, Observer, throwError } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, take } from 'rxjs/operators';
import { LogLevel } from '../diagnostics/log-level';
import { Logging } from '../diagnostics/logging';
import { Strings } from '../generated/strings';
import { AuthorizationManager } from '../security/authorization-manager';
import { CimKeyProperties, CimMultiple, CimResult, CimSingle } from './cim';
import { NodeCimOutput, NodeRequestOptions } from './node-connection';
import { PowerShellAlternate } from './powershell-alternate';
import { PowerShellStream } from './powershell-stream';
import {
    WebsocketStream,
    WebsocketStreamConnectionState,
    WebsocketStreamDataRequestState,
    WebsocketStreamDataState,
    WebsocketStreamDataTarget,
    WebsocketStreamHandler,
    WebsocketStreamName,
    WebsocketStreamProcessor
} from './websocket-stream';

/**
 * Cim result format.
 */
export enum CimResultFormat {
    /**
     * Single instance result.
     */
    Single,

    /**
     * Multiple instances result.
     */
    Multiple,

    /**
     * Mixed data result.
     */
    Result
}

/**
 * Cim Stream options.
 */
export interface CimStreamOptions extends NodeRequestOptions {
    /**
     * Partial data response. By default, it responses back with completed data.
     */
    partial?: boolean;

    /**
     * Override Query ID so it can request cancel.
     */
    queryId?: string;

    /**
     * Buffering time period by milliseconds.
     */
    bufferTime?: number;

    /**
     * Buffering count.
     */
    bufferCount?: number;
}

/**
 * CIM Get instance multiple.
 */
export interface CimGetInstanceMultiple {
    name: 'CimGetInstanceMultiple';
    namespace: string;
    className: string;
}

/**
 * Cim Get instance single.
 */
export interface CimGetInstanceSingle {
    name: 'CimGetInstanceSingle';
    namespace: string;
    className: string;
    keyProperties: CimKeyProperties;
}

/**
 * Cim Invoke method instance.
 */
export interface CimInvokeMethodInstance {
    name: 'CimInvokeMethodInstance';
    namespace: string;
    className: string;
    methodName: string;
    keyProperties: CimKeyProperties;
    data?: any;
}

/**
 * Cim Invoke method static.
 */
export interface CimInvokeMethodStatic {
    name: 'CimInvokeMethodStatic';
    namespace: string;
    className: string;
    methodName: string;
    data?: any;
}

/**
 * Cim Set instance.
 */
export interface CimSetInstance {
    name: 'CimSetInstance';
    namespace: string;
    className: string;
    keyProperties: CimKeyProperties;
    data: any;
}

/**
 * Cim Modify instance.
 */
export interface CimModifyInstance {
    name: 'CimModifyInstance';
    namespace: string;
    className: string;
    keyProperties: CimKeyProperties;
    data: any;
}

/**
 * Cim Delete instance.
 */
export interface CimDeleteInstance {
    name: 'CimDeleteInstance';
    namespace: string;
    className: string;
    keyProperties: CimKeyProperties;
}

/**
 * Cim Get instance query.
 */
export interface CimGetInstanceQuery {
    name: 'CimGetInstanceQuery';
    namespace: string;
    query: string;
}

/**
 * Cim Stream request data.
 */
export type CimStreamRequestData =
    CimGetInstanceMultiple
    | CimGetInstanceSingle
    | CimInvokeMethodInstance
    | CimInvokeMethodStatic
    | CimSetInstance
    | CimModifyInstance
    | CimDeleteInstance
    | CimGetInstanceQuery;

/**
 * The request packet of Cim Stream to the gateway.
 */
export interface CimStreamRequest {
    /**
     * The identification string (auto generated or supplied as queryId option.)
     */
    id: string;

    /**
     * The stream target.
     */
    target: WebsocketStreamDataTarget;

    /**
     * The date request state.
     */
    requestState: WebsocketStreamDataRequestState;

    /**
     * The request data.
     */
    request: CimStreamRequestData;

    /**
     * The Cim stream options.
     */
    options?: CimStreamOptions;
}

/**
 * Cim stream response.
 */
export interface CimStreamResponse {
    /**
     * Request ID from client.
     */
    id: string;

    /**
     * The state of response data.
     */
    state: WebsocketStreamDataState;

    /**
     * The index number of response data.
     */
    index: number;

    /**
     * The response data.
     */
    response: any;
}

/**
 * Cim Processor interface. Each Cim query creates new observable.
 */
class CimProcessor extends WebsocketStreamProcessor<CimSingle | CimMultiple | CimResult, CimStreamOptions> {
    /**
     * Holding result if waitCompleted option is specified for multiple instances.
     */
    declare public response?: any;

    /**
     * Initializes a new instance of the CimProcessor class.
     * @param observer Observer to send back result to caller.
     * @param target Stream Target object.
     * @param format CIM result format.
     * @param options Options for Cim stream query.
     */
    constructor(
        observer: Observer<CimSingle | CimMultiple | CimResult>,
        target: WebsocketStreamDataTarget,
        public format: CimResultFormat,
        options: CimStreamOptions) {
        super(observer, target, options);
    }
}

export interface CimStreamMonitorSetContext<T> {
    nodeName: string;
    requestState: WebsocketStreamDataRequestState;
    request: CimStreamRequestData;
    format: CimResultFormat;
    options?: CimStreamOptions;
    data: T;
}

/**
 * Set of monitors for pre and post process of cim stream.
 */
export interface CimStreamMonitorSet<T> {
    name: string;
    preMonitor: (
        nodeName: string,
        requestState: WebsocketStreamDataRequestState,
        request: CimStreamRequestData,
        format: CimResultFormat,
        options?: CimStreamOptions) =>
            Observable<CimStreamMonitorSetContext<any>>;
    successMonitor: (result: T, context: CimStreamMonitorSetContext<any>) => Observable<T>;
    errorMonitor: (error: any, context: CimStreamMonitorSetContext<any>) => Observable<T>;
}

/**
 * The CIM stream class.
 */
export class CimStream implements WebsocketStreamHandler {
    private static readonly logSourceName = 'CimStream';
    /**
     * The collection of set of monitors.
     */
    private static monitorSets: CimStreamMonitorSet<any>[] = [];
    private processors: Map<string /* id */, CimProcessor> = new Map();
    private strings = MsftSme.getStrings<Strings>().MsftSmeShell.Core.WebsocketStream.CimStream;

    /**
     * Register the set of monitors.
     *
     * @param monitorSet The set of monitors.
     */
    public static registerMonitorSet(monitorSet: CimStreamMonitorSet<any>): void {
        const found = CimStream.monitorSets.find(monitors => monitors.name === monitorSet.name);
        if (found) {
            return;
        }

        CimStream.monitorSets.push(monitorSet);
    }

    /**
     * Unregister the set of monitors.
     *
     * @param name The name of set of monitors.
     * @returns boolean true if unregistered the named set.
     */
    public static unregisterMonitors(name: string): boolean {
        const found = CimStream.monitorSets.find(monitors => monitors.name === name);
        if (found) {
            CimStream.monitorSets.remove(found);
            return true;
        }

        return false;
    }

    /**
     * Initializes a new instance of the CimStream class.
     *
     * @param websocketStream the websocket stream object.
     * @param powerShellStream the PowerShell steam object.
     * @param authorizationManager the authorization manager object.
     */
    constructor(
        private websocketStream: WebsocketStream,
        private powerShellStream: PowerShellStream,
        private authorizationManager: AuthorizationManager) {
        websocketStream.registerProcessor(WebsocketStreamName.CimStreamName, this);
    }

    /**
     * CIM Get MultipleInstances
     *
     * @param nodeName the name of the node to use for this request
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param options the options for this request.
     * @return Observable<CimResult> the query observable.
     */
    public getInstanceMultiple(
            nodeName: string,
            namespace: string,
            className: string,
            options?: CimStreamOptions): Observable<CimMultiple> {
        this.setPowerShellParameters(nodeName, options, NodeCimOutput.Multiple);
        const observable = PowerShellAlternate.createStream<CimMultiple>(this.powerShellStream, nodeName, options);
        if (observable) {
            return observable;
        }

        const name = 'CimGetInstanceMultiple';
        const request = <CimGetInstanceMultiple>{ name, namespace, className };
        const requestState = WebsocketStreamDataRequestState.Normal;
        return this.monitorCreateRequest(nodeName, requestState, request, CimResultFormat.Multiple, options);
    }

    /**
     * CIM Get SingleInstance
     *
     * @param nodeName the name of the node to use for this request
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param keyProperties the key properties object.
     * @param options the options for this request.
     * @return Observable<any> the query observable.
     */
    public getInstanceSingle(
            nodeName: string,
            namespace: string,
            className: string,
            keyProperties: CimKeyProperties,
            options?: CimStreamOptions): Observable<CimSingle> {
        this.setPowerShellParameters(nodeName, options, NodeCimOutput.Multiple, keyProperties);
        const observable = PowerShellAlternate.createStream<CimSingle>(this.powerShellStream, nodeName, options);
        if (observable) {
            return observable;
        }

        const name = 'CimGetInstanceSingle';
        const request = <CimGetInstanceSingle>{ name, namespace, className, keyProperties };
        const requestState = WebsocketStreamDataRequestState.Normal;
        return this.monitorCreateRequest(nodeName, requestState, request, CimResultFormat.Single, options);
    }

    /**
     * CIM Invoke InstanceMethod
     *
     * @param nodeName the name of the node to use for this request
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param methodName the method name.
     * @param keyProperties the key properties object.
     * @param data the method input data.
     * @param options the options for this request.
     * @return Observable<any> the query observable.
     */
    public invokeMethodInstance(
            nodeName: string,
            namespace: string,
            className: string,
            methodName: string,
            keyProperties: any,
            data?: any,
            options?: CimStreamOptions): Observable<CimResult> {
        this.setPowerShellParameters(nodeName, options, NodeCimOutput.Multiple, keyProperties, data && data.parameters);
        const observable = PowerShellAlternate.createStream<CimResult>(this.powerShellStream, nodeName, options);
        if (observable) {
            return observable;
        }

        const name = 'CimInvokeMethodInstance';
        const request = <CimInvokeMethodInstance>{ name, namespace, className, methodName, keyProperties, data };
        const requestState = WebsocketStreamDataRequestState.Normal;
        return this.monitorCreateRequest(nodeName, requestState, request, CimResultFormat.Result, options);
    }

    /**
     * CIM Invoke StaticMethod
     *
     * @param nodeName the name of the node to use for this request
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param methodName the method name.
     * @param data the method input data.
     * @param options the options for this request.
     * @return Observable<any> the query observable.
     */
    public invokeMethodStatic(
        nodeName: string,
        namespace: string,
        className: string,
        methodName: string,
        data?: any,
        options?: CimStreamOptions): Observable<CimResult> {
        this.setPowerShellParameters(nodeName, options, NodeCimOutput.Result, data && data.parameters);
        const observable = PowerShellAlternate.createStream<CimResult>(this.powerShellStream, nodeName, options);
        if (observable) {
            return observable;
        }

        const name = 'CimInvokeMethodStatic';
        const request = <CimInvokeMethodStatic>{ name, namespace, className, methodName, data };
        const requestState = WebsocketStreamDataRequestState.Normal;
        return this.monitorCreateRequest(nodeName, requestState, request, CimResultFormat.Result, options);
    }

    /**
     * CIM Set SingleInstance
     *
     * @param nodeName the name of the node to use for this request
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param keyProperties the key properties object.
     * @param data the method input data.
     * @param options the options for this request.
     * @return Observable<any> the query observable.
     */
    public setInstance(
            nodeName: string,
            namespace: string,
            className: string,
            keyProperties: any,
            data: any,
            options?: CimStreamOptions): Observable<CimSingle> {
        this.setPowerShellParameters(nodeName, options, NodeCimOutput.Single, keyProperties, data && data.properties);
        const observable = PowerShellAlternate.createStream<CimSingle>(this.powerShellStream, nodeName, options);
        if (observable) {
            return observable;
        }

        const name = 'CimSetInstance';
        const request = <CimSetInstance>{ name, namespace, className, keyProperties, data };
        const requestState = WebsocketStreamDataRequestState.Normal;
        return this.monitorCreateRequest(nodeName, requestState, request, CimResultFormat.Single, options);
    }

    /**
     * CIM Modify SingleInstance
     *
     * @param nodeName the name of the node to use for this request
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param keyProperties the key properties object.
     * @param data the method input data.
     * @param options the options for this request.
     * @return Observable<any> the query observable.
     */
    public modifyInstance(
            nodeName: string,
            namespace: string,
            className: string,
            keyProperties: any,
            data: any,
            options?: CimStreamOptions): Observable<CimSingle> {
        this.setPowerShellParameters(nodeName, options, NodeCimOutput.Single, keyProperties, data && data.properties);
        const observable = PowerShellAlternate.createStream<CimSingle>(this.powerShellStream, nodeName, options);
        if (observable) {
            return observable;
        }

        const name = 'CimModifyInstance';
        const request = <CimModifyInstance>{ name, namespace, className, keyProperties, data };
        const requestState = WebsocketStreamDataRequestState.Normal;
        return this.monitorCreateRequest(nodeName, requestState, request, CimResultFormat.Single, options);
    }

    /**
     * CIM Delete SingleInstance
     *
     * @param nodeName the name of the node to use for this request
     * @param namespace the cim namespace.
     * @param className the class name.
     * @param keyProperties the key properties object.
     * @param options the options for this request.
     * @return Observable<any> the query observable.
     */
    public deleteInstance(
            nodeName: string,
            namespace: string,
            className: string,
            keyProperties: any,
            options?: CimStreamOptions): Observable<CimSingle> {
        this.setPowerShellParameters(nodeName, options, NodeCimOutput.Single, keyProperties);
        const observable = PowerShellAlternate.createStream<CimSingle>(this.powerShellStream, nodeName, options);
        if (observable) {
            return observable;
        }

        const name = 'CimDeleteInstance';
        const request = <CimDeleteInstance>{ name, namespace, className, keyProperties };
        const requestState = WebsocketStreamDataRequestState.Normal;
        return this.monitorCreateRequest(nodeName, requestState, request, CimResultFormat.Single, options);
    }

    /**
     * CIM Submit WqlQuery
     *
     * @param nodeName the name of the node to use for this request
     * @param namespace the cim namespace.
     * @param query the WQL string.
     * @param options the options for this request.
     * @return Observable<any> the query observable.
     */
    public getInstanceQuery(
            nodeName: string,
            namespace: string,
            query: string,
            options?: CimStreamOptions): Observable<CimResult> {
        this.setPowerShellParameters(nodeName, options, NodeCimOutput.Query, options && options.powerShellParameters);
        const observable = PowerShellAlternate.createStream<CimResult>(this.powerShellStream, nodeName, options);
        if (observable) {
            return observable;
        }

        const name = 'CimGetInstanceQuery';
        const request = <CimGetInstanceQuery>{ name, namespace, query };
        const requestState = WebsocketStreamDataRequestState.Normal;
        return this.monitorCreateRequest(nodeName, requestState, request, CimResultFormat.Multiple, options);
    }

    /**
     * Cancel active CIM query.
     * Result response comes back to the original query to end.
     *
     * @param nodeName the node name.
     * @param id the id of original request specified as options.queryId.
     */
    public cancel(nodeName: string, id: string): void {
        const target = this.websocketStream.getTarget(this.authorizationManager, nodeName);
        const requestState = WebsocketStreamDataRequestState.Cancel;
        const request = <CimStreamRequest>{ id, target, requestState, request: null };
        this.websocketStream.sendNext(WebsocketStreamName.CimStreamName, request);
    }

    /**
     * Reset data for connection cleanup.
     */
    public reset(): void {
        Logging.log({ level: LogLevel.Warning, message: this.strings.ResetError.message, source: CimStream.logSourceName });
        const processors: CimProcessor[] = [];
        this.processors.forEach((value) => processors.push(value));
        this.processors.clear();
        processors.forEach((processor) => {
            processor.error(new Error(this.strings.ResetError.message));
        });
    }

    /**
     * Process the socket message.
     *
     * @param message the socket message.
     */
    public process(message: CimStreamResponse): void {
        if (!message) {
            throw new Error(this.strings.NoContentError.message);
        }

        const processor = this.processors.get(message.id);
        if (!processor) {
            Logging.log({ level: LogLevel.Warning, message: this.strings.UnexpectedReceivedError.message, source: CimStream.logSourceName });
            return;
        }

        switch (message.state) {
            case WebsocketStreamDataState.Data:
                this.operationNext(processor, message.response);
                break;

            case WebsocketStreamDataState.Completed:
                this.operationComplete(processor, message.response);
                this.operationEnd(message.id);
                break;

            case WebsocketStreamDataState.Error:
                this.operationError(processor, { xhr: message });
                this.operationEnd(message.id);
                break;

            case WebsocketStreamDataState.Noop:
                break;
        }
    }

    private operationNext(processor: CimProcessor, response: any): boolean {
        const partial = processor.options && processor.options.partial;
        if (!response) {
            return !partial;
        }

        // buffering result.
        if (!partial) {
            if (processor.format === CimResultFormat.Single) {
                // expecting - { response: any }
                processor.response = response;
            } else if (processor.format === CimResultFormat.Multiple) {
                // expecting - { response: { value: any[]; } } format.
                if (!response || !response.value) {
                    Logging.log(
                        { level: LogLevel.Error, message: this.strings.UnexpectedMultipleError.message, source: CimStream.logSourceName });
                } else {
                    if (!processor.response) {
                        processor.response = response;
                    } else {
                        response.value.forEach(value => processor.response.value.push(value));
                    }
                }
            } else if (processor.format === CimResultFormat.Result) {
                // expecting - { response: { results: any[] }, <name1>: <value1>, <name2>: <value2> } format.
                if (!processor.response) {
                    processor.response = response;
                } else {
                    if (response.results) {
                        if (processor.response.results) {
                            response.results.forEach(value => processor.response.results.push(value));
                            response.results = undefined;
                        }
                    }

                    // merge other properties.
                    processor.response = { ...processor.response, ...response };
                }
            }
        } else {
            processor.next(response);
        }

        return !partial;
    }

    private operationComplete(processor: CimProcessor, response: CimSingle | CimMultiple | CimResult): void {
        if (this.operationNext(processor, response)) {
            // complete mode to send all result once.
            processor.next(processor.response);
        }

        if (!processor.sendOnce) {
            // send null if no result was produced but success.
            processor.next(null);
        }

        processor.complete();
    }

    private operationError(processor: CimProcessor, error: any): void {
        processor.error(error);
    }

    private operationEnd(id: string): void {
        this.processors.delete(id);
    }

    private createRequest<T>(
        nodeName: string,
        requestState: WebsocketStreamDataRequestState,
        request: CimStreamRequestData,
        format: CimResultFormat,
        options?: CimStreamOptions): Observable<T> {
        // publish object is created two ways.
        // 1) socket is connected so submit the request immediately with simple observable.
        //   (if-block and this is the most of cases.)
        // 2) socket is not connected so wait for the socket to ready and submit request with
        //    complex observable. Initial connect and re-connection takes this observable.
        //   (else-block and this is a few cases.)
        let publish: Observable<T>;
        if (this.websocketStream.socketStateRaw === WebsocketStreamConnectionState.Connected) {
            publish = this.createRequestSimple(nodeName, requestState, request, format, options);
        } else {
            publish = this.websocketStream.socketState
                .pipe(
                    filter(state => state === WebsocketStreamConnectionState.Connected
                        || state === WebsocketStreamConnectionState.Failed
                        || state === WebsocketStreamConnectionState.NotConfigured),
                    take(1),
                    mergeMap(state => {
                        if (state === WebsocketStreamConnectionState.Connected) {
                            return this.createRequestSimple<T>(nodeName, requestState, request, format, options);
                        }

                        return throwError(() => new Error(this.strings.ConnectionError.message));
                    }));
        }

        return publish
            .pipe(
                catchError((error) => {
                    // retry if reset connection of socket was observed.
                    if (error && error.message === this.strings.ResetError.message) {
                        return <Observable<T>>this.monitorCreateRequest(nodeName, requestState, request, format, options);
                    }

                    if ((!options || options.noAuth !== true)
                        && !this.authorizationManager.signOnManager.isSignOnTokenEnabled
                        && this.authorizationManager.canHandleStreamFailure(error.xhr)) {
                        return this.authorizationManager.handleStreamFailure(nodeName, options, error.xhr)
                            .pipe(switchMap(updatedOptions =>
                                <Observable<T>>this.monitorCreateRequest(nodeName, requestState, request, format, updatedOptions)));
                    }

                    if (this.authorizationManager.signOnManager.isSignOnTokenEnabled
                        && this.authorizationManager.signOnManager.canHandleStreamUnauthorizedLogin(error.xhr)) {
                        return this.authorizationManager.signOnManager.handleStreamUnauthorizedLogin(options, error.xhr)
                            .pipe(switchMap(updatedOptions =>
                                <Observable<T>>this.monitorCreateRequest(nodeName, requestState, request, format, updatedOptions)));
                    }

                    return throwError(() => error);
                }));
    }

    private createRequestSimple<T>(
        nodeName: string,
        requestState: WebsocketStreamDataRequestState,
        request: CimStreamRequestData,
        format: CimResultFormat,
        options?: CimStreamOptions): Observable<T> {
        return new Observable(observer => {
            const target = this.websocketStream.getTarget(this.authorizationManager, nodeName);
            const id = this.sendRequest(observer, target, requestState, request, format, options);
            return () => {
                const processor = this.processors.get(id);
                if (processor) {
                    processor.end = true;
                    if (!processor.closed && !processor.closing) {
                        this.cancel(processor.target.nodeName, id);
                    }
                }
            };
        });
    }

    private sendRequest(
        observer: Observer<any>,
        target: WebsocketStreamDataTarget,
        requestState: WebsocketStreamDataRequestState,
        request: CimStreamRequestData,
        format: CimResultFormat,
        options?: CimStreamOptions): string {
        const id = (options && options.queryId) || MsftSme.getUniqueId();
        const processor = new CimProcessor(observer, target, format, options);
        this.processors.set(id, processor);
        this.websocketStream.sendNext(WebsocketStreamName.CimStreamName, <CimStreamRequest>{ id, target, requestState, request, options });
        return id;
    }

    /**
     * Set PowerShell parameters to the options object.
     *
     * @param options The node request options.
     * @param outputType The output data type.
     * @param keys The key data.
     * @param data The arguments data.
     */
    private setPowerShellParameters(
            nodeName: string,
            options: NodeRequestOptions,
            cimOutput: NodeCimOutput,
            keys?: any,
            data?: any): void {
        if (options == null || options.powerShell == null) {
            return;
        }

        options.powerShellContext = {
            cimOutput,
            parameters: { ...(keys || {}), ...(data || {}) }
        };

        options.powerShellEndpoint = this.authorizationManager.getJeaEndpoint(nodeName);
    }

    private monitorCreateRequest<T>(
        nodeName: string,
        requestState: WebsocketStreamDataRequestState,
        request: CimStreamRequestData,
        format: CimResultFormat,
        options?: CimStreamOptions): Observable<T> {
        let monitored: (
            nodeName: string,
            requestState: WebsocketStreamDataRequestState,
            request: CimStreamRequestData,
            format: CimResultFormat,
            options?: CimStreamOptions) => Observable<T> =
                (nodeName1: string,
                requestState1: WebsocketStreamDataRequestState,
                request1: CimStreamRequestData,
                format1: CimResultFormat,
                options1?: CimStreamOptions) => this.createRequest(nodeName1, requestState1, request1, format1, options1);
        for (const monitorSet of CimStream.monitorSets) {
            monitored = this.monitor<T>(monitored, monitorSet);
        }

        return monitored(nodeName, requestState, request, format, options);
    }

    private monitor<T>(
        target: (
            nodeName: string,
            requestState: WebsocketStreamDataRequestState,
            request: CimStreamRequestData,
            format: CimResultFormat,
            options?: CimStreamOptions) => Observable<T>,
        monitorSet: CimStreamMonitorSet<T>): (
            nodeName: string,
            requestState: WebsocketStreamDataRequestState,
            request: CimStreamRequestData,
            format: CimResultFormat,
            options?: CimStreamOptions) => Observable<T> {

        return function(
            nodeName: string,
            requestState: WebsocketStreamDataRequestState,
            request: CimStreamRequestData,
            format: CimResultFormat,
            options?: CimStreamOptions): Observable<T> {
            let context: CimStreamMonitorSetContext<any>;
            return monitorSet.preMonitor(nodeName, requestState, request, format, options)
                .pipe(
                    switchMap(packet => {
                        context = packet;
                        return target(packet.nodeName, packet.requestState, packet.request, packet.format, packet.options);
                    }),
                    catchError((error: any) => monitorSet.errorMonitor(error, context)),
                    switchMap(response => monitorSet.successMonitor(response, context))
                );
        };
    }
}
